import { useCallback, useEffect, useState } from 'react';
import { Button, KpiCard } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { PendentesTabela } from '@/components/Certificacao/PendentesTabela';
import { RevisaoPanel } from '@/components/Certificacao/RevisaoPanel';
import { mapCertificacaoError } from '@/components/Certificacao/messages';
import {
  assinarLaudo,
  assumirRequisicao,
  CertificacaoError,
  type ChecklistRevisao,
  type CertificacoesPendentes,
  type CertificacaoPendente,
  getPendentes,
} from '@/lib/certificacao';

const t = ptBR.certificacao;

function _mensagem(err: unknown): string {
  return mapCertificacaoError(err instanceof CertificacaoError ? err.reason : null);
}

export function CertificacaoPage() {
  const apiClient = useApiClient();
  const [pendentes, setPendentes] = useState<CertificacoesPendentes | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [itemAberto, setItemAberto] = useState<CertificacaoPendente | null>(null);
  const [abrindoId, setAbrindoId] = useState<string | null>(null);
  const [assinando, setAssinando] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [coincidencia, setCoincidencia] = useState(false);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setErroLista(null);
    try {
      setPendentes(await getPendentes(apiClient));
    } catch (err) {
      setPendentes(null);
      setErroLista(_mensagem(err));
    } finally {
      setCarregando(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // Abrir a revisão É assumir o item: sem isso o certificador preencheria o
  // checklist para descobrir no clique final que o item é de outra pessoa. A
  // disputa (409) aparece aqui, antes do trabalho.
  async function handleAbrirRevisao(item: CertificacaoPendente) {
    setErroAcao(null);
    setSucesso(null);
    setAbrindoId(item.requisicao_id);
    try {
      await assumirRequisicao(apiClient, item.requisicao_id);
      setItemAberto(item);
    } catch (err) {
      setItemAberto(null);
      setErroAcao(_mensagem(err));
      // A lista mudou por baixo (outro assumiu, ou já assinaram):
      // recarregar é o que devolve a tela à verdade do servidor.
      void recarregar();
    } finally {
      setAbrindoId(null);
    }
  }

  async function handleAssinar(checklist: ChecklistRevisao) {
    if (itemAberto === null) return;
    setAssinando(true);
    setErroAcao(null);
    try {
      const assinatura = await assinarLaudo(apiClient, itemAberto.requisicao_id, checklist);
      setItemAberto(null);
      setSucesso(t.assinaturaOk);
      setCoincidencia(assinatura.mesmo_autor_producao);
      await recarregar();
    } catch (err) {
      setErroAcao(_mensagem(err));
    } finally {
      setAssinando(false);
    }
  }

  return (
    <section
      data-testid="certificacao-page"
      className="mx-auto flex w-full max-w-5xl flex-col gap-sc-6"
    >
      <header>
        <p className="mb-1.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
          {t.eyebrow}
        </p>
        <h1 className="text-display-md text-fg">{t.pageTitle}</h1>
        <p className="mt-2 max-w-2xl text-body-sm text-fg-muted">{t.intro}</p>
      </header>

      {pendentes !== null ? <Indicadores indicadores={pendentes.indicadores} /> : null}

      {sucesso !== null ? (
        <div
          role="status"
          data-testid="certificacao-sucesso"
          className="border border-success bg-success-50 px-sc-3 py-2 text-body-sm text-success-700"
        >
          <p>{sucesso}</p>
          {coincidencia ? (
            <p data-testid="certificacao-coincidencia" className="mt-1">
              {t.assinaturaCoincidencia}
            </p>
          ) : null}
        </div>
      ) : null}

      {erroAcao !== null && itemAberto === null ? (
        <p
          role="alert"
          data-testid="certificacao-erro-acao"
          className="border border-danger bg-danger-50 px-sc-3 py-2 text-body-sm text-danger-700"
        >
          {erroAcao}
        </p>
      ) : null}

      {itemAberto !== null ? (
        <RevisaoPanel
          item={itemAberto}
          assinando={assinando}
          erro={erroAcao}
          onAssinar={(checklist) => void handleAssinar(checklist)}
          onFechar={() => {
            setItemAberto(null);
            setErroAcao(null);
          }}
        />
      ) : null}

      <ListaSecao
        carregando={carregando}
        erro={erroLista}
        itens={pendentes?.itens ?? []}
        itemAberto={itemAberto?.requisicao_id ?? null}
        abrindoId={abrindoId}
        onAbrirRevisao={(item) => void handleAbrirRevisao(item)}
        onRetry={() => void recarregar()}
      />
    </section>
  );
}

interface ListaSecaoProps {
  carregando: boolean;
  erro: string | null;
  itens: CertificacaoPendente[];
  itemAberto: string | null;
  abrindoId: string | null;
  onAbrirRevisao: (item: CertificacaoPendente) => void;
  onRetry: () => void;
}

/** Carregando / erro com nova tentativa / tabela — os três estados da lista. */
function ListaSecao({
  carregando,
  erro,
  itens,
  itemAberto,
  abrindoId,
  onAbrirRevisao,
  onRetry,
}: ListaSecaoProps) {
  if (carregando) {
    return (
      <p data-testid="pendentes-loading" className="text-body-sm text-fg-muted">
        {t.pendentesLoading}
      </p>
    );
  }
  if (erro !== null) {
    return (
      <div>
        <p data-testid="pendentes-error" className="text-body-sm text-danger-700">
          {erro}
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          data-testid="pendentes-retry"
          onClick={onRetry}
          className="mt-sc-2"
        >
          {t.pendentesRetry}
        </Button>
      </div>
    );
  }
  return (
    <PendentesTabela
      itens={itens}
      itemAberto={itemAberto}
      abrindoId={abrindoId}
      onAbrirRevisao={onAbrirRevisao}
    />
  );
}

function Indicadores({ indicadores }: { indicadores: CertificacoesPendentes['indicadores'] }) {
  return (
    <div
      className="grid grid-cols-1 gap-sc-4 sm:grid-cols-3"
      data-testid="certificacao-indicadores"
    >
      <KpiCard
        title={t.indicadorPendentes}
        value={indicadores.na_fila}
        data-testid="indicador-pendentes"
      />
      <KpiCard
        title={t.indicadorVencemHoje}
        value={indicadores.vencem_hoje}
        data-testid="indicador-vencem-hoje"
      />
      <KpiCard
        title={t.indicadorAssinadosHoje}
        value={indicadores.assinados_hoje}
        data-testid="indicador-assinados-hoje"
      />
    </div>
  );
}

export default CertificacaoPage;
